import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, PartyPopper, BellRing } from 'lucide-react';
import { getVenueImage } from './venueImage';
import { EventFavoriteButton } from './EventFavoriteButton';
import { LoadingVideo } from '../LoadingVideo';
import { useNavigate } from 'react-router-dom';
import { fetchAccountSettings } from '../../utils/accountSettingsApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const font = "'Inria Serif', serif";

const CATEGORY_DISPLAY = {
  karaoke: 'Karaoke',
  'live-music': 'Live Music',
  'dj-set': 'DJ Set',
  party: 'Party',
  'greek-night': 'Greek Night',
  'wine-tasting': 'Wine Tasting',
  'cultural-event': 'Cultural Event',
  other: 'Event',
};

function resolveMediaSrc(url) {
  if (!url) return null;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
}

function resolveVenueImage(venue, idx = 0) {
  if (!venue) return null;
  const directPhoto = venue.main_photo || (Array.isArray(venue.photo_urls) && venue.photo_urls[0]);
  const resolved = resolveMediaSrc(directPhoto);
  if (resolved) return resolved;
  // Final fallback — same pool the guest-map venue cards use, keyed by venue id
  // so each venue gets a stable image.
  const seed = (venue.id || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), idx);
  return getVenueImage(venue, seed);
}

function nowDate() {
  return new Date();
}

/**
 * Returns a friendly relative time string for an event card given the event's
 * start_at_utc / end_at_utc ISO strings. Examples:
 *   "Live Now · ends 23:30"
 *   "Starting Now"
 *   "Tonight · 21:00"
 *   "Tomorrow · 21:00"
 *   "This Friday · 21:00"
 *   "Next Tue · 21:00"
 *
 * Hidden 30 min before end (handled server-side; double-check here for safety).
 */
export function relativeEventLabel(event) {
  if (!event?.start_at_utc || !event?.end_at_utc) return '';
  const start = new Date(event.start_at_utc);
  const end = new Date(event.end_at_utc);
  const now = nowDate();
  const startTimeStr = event.start_time || '';
  const endTimeStr = event.end_time || '';

  // Live now (and at least 30 min remaining).
  if (now >= start && now < new Date(end.getTime() - 30 * 60 * 1000)) {
    return endTimeStr ? `Live Now · ends ${endTimeStr}` : 'Live Now';
  }
  // Starting soon (within 30 min of start).
  if (now < start && (start.getTime() - now.getTime()) <= 30 * 60 * 1000) {
    return 'Starting Now';
  }

  // Same calendar day as start (in user's tz, good enough).
  const startDayLocal = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayDiff = Math.round((startDayLocal - todayLocal) / 86400000);
  if (dayDiff === 0) return startTimeStr ? `Tonight · ${startTimeStr}` : 'Tonight';
  if (dayDiff === 1) return startTimeStr ? `Tomorrow · ${startTimeStr}` : 'Tomorrow';
  if (dayDiff > 1 && dayDiff <= 6) {
    const weekday = start.toLocaleDateString('en-GB', { weekday: 'short' });
    return startTimeStr ? `This ${weekday} · ${startTimeStr}` : `This ${weekday}`;
  }
  const weekday = start.toLocaleDateString('en-GB', { weekday: 'short' });
  return startTimeStr ? `Next ${weekday} · ${startTimeStr}` : `Next ${weekday}`;
}

/** Group events by venue id, soonest event first within each group AND
 * across groups (by checking the soonest event of each venue). */
function groupByVenue(events) {
  const map = new Map();
  for (const ev of events) {
    const vid = ev.venue?.id;
    if (!vid) continue;
    if (!map.has(vid)) map.set(vid, []);
    map.get(vid).push(ev);
  }
  // Sort events within each venue by start_at_utc asc (soonest first).
  for (const arr of map.values()) {
    arr.sort((a, b) => String(a.start_at_utc).localeCompare(String(b.start_at_utc)));
  }
  // Then sort venues by their soonest event start_at_utc asc.
  return Array.from(map.values()).sort((a, b) =>
    String(a[0]?.start_at_utc || '').localeCompare(String(b[0]?.start_at_utc || ''))
  );
}

function VenueEventCard({ events, onVenueClick }) {
  const [index, setIndex] = useState(0);
  const event = events[index] || events[0];
  const venue = event?.venue;
  const mediaUrl = resolveMediaSrc(event?.media_url);
  const mediaType = event?.media_type;
  const fallbackImage = resolveVenueImage(venue, index);
  const showVideo = mediaType === 'video' && mediaUrl;
  const showImage = mediaType === 'photo' && mediaUrl;
  const hasMultiple = events.length > 1;
  const title = event?.display_title || CATEGORY_DISPLAY[event?.category] || 'Event';
  const label = relativeEventLabel(event);
  const isLive = label.startsWith('Live Now');

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`map-event-card-${venue?.id || index}`}
      onClick={() => onVenueClick?.(venue?.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onVenueClick?.(venue?.id);
        }
      }}
      style={{
        flex: '0 0 78%',
        maxWidth: 280,
        scrollSnapAlign: 'start',
        cursor: 'pointer',
      }}
    >
      <div style={{ position: 'relative', borderRadius: 22, overflow: 'hidden', boxShadow: '0 14px 28px rgba(20, 16, 50, 0.20)', height: 170 }}>
        {showVideo ? (
          <LoadingVideo
            dataTestId={`map-event-card-video-${venue?.id}`}
            src={mediaUrl}
            wrapperStyle={{ width: '100%', height: '100%' }}
          />
        ) : (
          <img
            data-testid={`map-event-card-image-${venue?.id}`}
            src={showImage ? mediaUrl : fallbackImage}
            alt={title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
        {/* Bottom gradient */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(10, 8, 30, 0.82) 100%)' }} />

        {/* Top-right: reminder bell + relative time badge */}
        <div
          style={{
            position: 'absolute', top: 10, right: 10,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {event?.id ? (
            <EventFavoriteButton eventId={event.id} size={26} testIdPrefix={`map-event-favorite-${venue?.id || ''}`} />
          ) : null}
          {label ? (
            <div
              data-testid={`map-event-card-time-${venue?.id}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 10px', borderRadius: 999,
                background: isLive ? 'rgba(255, 80, 80, 0.92)' : 'rgba(18,18,28,0.62)',
                color: '#FFFFFF',
                fontFamily: font, fontSize: 11.5, fontWeight: 700,
                backdropFilter: 'blur(6px)',
                boxShadow: isLive ? '0 6px 14px rgba(255, 80, 80, 0.4)' : 'none',
              }}
            >
              {isLive ? (
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--surface)', boxShadow: '0 0 6px rgba(255,255,255,0.9)' }} />
              ) : null}
              {label}
            </div>
          ) : null}
        </div>

        {/* Top-left venue name pill */}
        {venue?.name ? (
          <div
            data-testid={`map-event-card-venue-${venue.id}`}
            style={{
              position: 'absolute', top: 10, left: 10,
              padding: '6px 10px', borderRadius: 999,
              background: 'rgba(18,18,28,0.55)', color: '#FFFFFF',
              fontFamily: font, fontSize: 11, fontWeight: 700,
              backdropFilter: 'blur(6px)',
              maxWidth: '60%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {venue.name}
          </div>
        ) : null}

        {/* Bottom event title + mini description */}
        <div style={{ position: 'absolute', left: 12, right: 12, bottom: 10, color: '#FFFFFF' }}>
          <p style={{ margin: 0, fontFamily: font, fontSize: 18, fontWeight: 700, lineHeight: 1.1, textShadow: '0 2px 8px rgba(0,0,0,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }}>{title}</p>
          {event?.mini_description ? (
            <p style={{ margin: '4px 0 0', fontFamily: font, fontSize: 12.5, fontWeight: 600, opacity: 0.92, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word', lineHeight: 1.3 }}>
              {event.mini_description}
            </p>
          ) : null}
        </div>

        {/* Swipe arrows when multiple events from same venue */}
        {hasMultiple ? (
          <>
            <span
              role="button"
              data-testid={`map-event-card-prev-${venue?.id}`}
              onClick={(e) => { stop(e); setIndex((i) => (i - 1 + events.length) % events.length); }}
              style={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                width: 28, height: 28, borderRadius: '50%',
                background: 'rgba(18,18,28,0.55)', color: '#FFFFFF',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', backdropFilter: 'blur(6px)',
              }}
            >
              <ChevronLeft size={16} strokeWidth={2.4} />
            </span>
            <span
              role="button"
              data-testid={`map-event-card-next-${venue?.id}`}
              onClick={(e) => { stop(e); setIndex((i) => (i + 1) % events.length); }}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                width: 28, height: 28, borderRadius: '50%',
                background: 'rgba(18,18,28,0.55)', color: '#FFFFFF',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', backdropFilter: 'blur(6px)',
              }}
            >
              <ChevronRight size={16} strokeWidth={2.4} />
            </span>
            {/* Dots indicator */}
            <div style={{ position: 'absolute', bottom: 4, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 4 }}>
              {events.map((_, i) => (
                <span
                  key={i}
                  style={{
                    width: 5, height: 5, borderRadius: '50%',
                    background: i === index ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
                  }}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export function EventsCarousel({ events, onVenueClick }) {
  // Auto-scrolling slow carousel — only kicks in when there are enough events
  // that overflow horizontally. Pauses on hover/touch.
  const navigate = useNavigate();
  const scrollerRef = useRef(null);
  const [paused, setPaused] = useState(false);
  // "Event reminders are off" nudge. Renders ONLY when the guest has turned
  // OFF the in-app Event-reminders toggle, so they're aware reminders are
  // muted. Tapping it opens the notification settings page to re-enable.
  const [eventRemindersOff, setEventRemindersOff] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAccountSettings()
      .then((settings) => {
        if (cancelled) return;
        const prefs = settings?.notifications || {};
        setEventRemindersOff(prefs.pref_push_event_reminders === false);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const showPushNudge = eventRemindersOff;

  const grouped = useMemo(() => groupByVenue(events || []), [events]);

  useEffect(() => {
    if (paused) return undefined;
    const el = scrollerRef.current;
    if (!el) return undefined;
    if (el.scrollWidth <= el.clientWidth + 8) return undefined;
    let raf;
    const tick = () => {
      if (!el) return;
      // Slow auto scroll — ~12 px/sec
      el.scrollLeft += 0.2;
      if (el.scrollLeft >= el.scrollWidth - el.clientWidth - 1) {
        el.scrollLeft = 0;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused, grouped.length]);

  if (!grouped.length) return null;

  return (
    <div data-testid="map-events-carousel" style={{ margin: '14px 0 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 26, height: 26, borderRadius: 8, background: 'linear-gradient(135deg, rgba(255, 184, 76, 0.20), rgba(255, 122, 89, 0.22))', color: '#C85A1E', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <PartyPopper size={13} strokeWidth={2.4} />
          </span>
          <p style={{ margin: 0, fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Happening this week</p>
        </div>
      </div>
      {showPushNudge ? (
        <button
          type="button"
          data-testid="events-push-nudge"
          onClick={() => { navigate('/guest/settings/notifications'); }}
          style={{
            margin: '0 18px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 12,
            border: '1px solid rgba(200, 90, 30, 0.18)',
            background: 'linear-gradient(135deg, rgba(255, 184, 76, 0.10), rgba(255, 122, 89, 0.10))',
            color: '#7A3A12',
            fontFamily: font,
            fontSize: 12.5,
            lineHeight: 1.35,
            cursor: 'pointer',
            textAlign: 'left',
            width: 'calc(100% - 36px)',
          }}
        >
          <BellRing size={14} strokeWidth={2.2} style={{ flexShrink: 0, color: '#C85A1E' }} />
          <span>
            <span style={{ fontWeight: 700 }}>Event reminders are off.</span>
            {' Turn them on to get buzzed 2 hours before your saved events.'}
          </span>
        </button>
      ) : null}
      <div
        ref={scrollerRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '4px 16px 14px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
      >
        {grouped.map((venueEvents) => (
          <VenueEventCard
            key={venueEvents[0].venue?.id || venueEvents[0].id}
            events={venueEvents}
            onVenueClick={onVenueClick}
          />
        ))}
      </div>
    </div>
  );
}
